import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  listProposalChangeRequests,
  createProposalChangeRequest,
  actionProposalChangeRequest,
} from "../../../services/contractorService";
import { renderCurrencyIcon } from "../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { statusStyleArray } from "../../OnGoing/Changelog/config";
import RichTextEditor from "../../ChangeRequest/components/RichTextEditor";
import CancelChangeRequestModal from "../../ChangeRequest/modals/CancelChangeRequestModal";

// ---------- shared styled primitives ----------
const PrimaryButton = styled(Button)(() => ({
  backgroundColor: "#4D6BF6",
  color: "#fff",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  boxShadow: "none",
  padding: "8px 18px",
  fontFamily: "Inter Tight",
  "&:hover": { backgroundColor: "#3B5BDB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#A8B5F8", color: "#fff" },
}));

const SecondaryButton = styled(Button)(() => ({
  backgroundColor: "#fff",
  color: "#0D0D0E",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  border: "1px solid #D7D8D9",
  boxShadow: "none",
  padding: "8px 18px",
  fontFamily: "Inter Tight",
  "&:hover": { backgroundColor: "#F9FAFB", boxShadow: "none" },
}));

const DangerOutlineButton = styled(Button)(() => ({
  backgroundColor: "#fff",
  color: "#DC2626",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "8px",
  border: "1px solid #DC2626",
  boxShadow: "none",
  padding: "4px 12px",
  fontFamily: "Inter Tight",
  fontSize: 13,
  minWidth: 0,
  "&:hover": {
    backgroundColor: "#FEF2F2",
    boxShadow: "none",
    border: "1px solid #DC2626",
  },
}));

// ---------- Pills ----------
const STATUS_COLORS = {
  DRAFT: { bg: "#F3F4F6", fg: "#374151" },
  AWAITING_HOME_OWNER_CONFIRMATION: { bg: "#FEF3C7", fg: "#92400E" },
  APPROVED_BY_HOME_OWNER: { bg: "#D1FAE5", fg: "#065F46" },
  REJECTED_BY_HOME_OWNER: { bg: "#FEE2E2", fg: "#7F1D1D" },
  CANCELLED: { bg: "#F3F4F6", fg: "#374151" },
};

const formatStatusLabel = (status) =>
  String(status || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const StatusPill = ({ status }) => {
  const palette = STATUS_COLORS[String(status || "").toUpperCase()] || {
    bg: "#F1F2F4",
    fg: "#6B7280",
  };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 999,
        backgroundColor: palette.bg,
        color: palette.fg,
        fontFamily: "Inter Tight",
        whiteSpace: "nowrap",
        letterSpacing: "0.2px",
      }}
    >
      {formatStatusLabel(status)}
    </Box>
  );
};

const statusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  return text
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// CR-specific dot colors. Each status gets its own hue so the listing
// reads at a glance — orange (awaiting), green (approved), red
// (rejected), grey (cancelled), blue (draft / fallback).
const CR_STATUS_DOT_COLORS = {
  DRAFT: "#4D6BF6",
  AWAITING_HOME_OWNER_CONFIRMATION: "#F59E0B",
  APPROVED_BY_HOME_OWNER: "#10B981",
  REJECTED_BY_HOME_OWNER: "#EF4444",
  CANCELLED: "#9CA3AF",
};

const StatusDot = ({ status }) => {
  const key = String(status || "").toUpperCase();
  const dotColor =
    CR_STATUS_DOT_COLORS[key] ||
    statusStyleArray.find((s) => String(s.status).toUpperCase() === key)
      ?.backgroundColor ||
    "#9CA3AF";
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box
        sx={{
          width: "15px",
          height: "15px",
          borderRadius: "50%",
          backgroundColor: dotColor,
          border: "3px solid #e5e5e5",
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: 14,
          ml: 1,
          color: "#0D0D0E",
          fontFamily: "Inter Tight",
        }}
      >
        {statusDisplayText(status)}
      </Typography>
    </Box>
  );
};

const TypePill = ({ type }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 10px",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 999,
      backgroundColor: "#EDE9FE",
      color: "#5B21B6",
      fontFamily: "Inter Tight",
      whiteSpace: "nowrap",
      letterSpacing: "0.2px",
    }}
  >
    {formatStatusLabel(type)}
  </Box>
);

const NotesCell = ({ html }) => {
  const text = String(html || "").trim();
  if (!text) return <Box sx={{ color: "#9CA3AF" }}>—</Box>;
  const plain = text.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  return (
    <Tooltip title={plain} arrow>
      <Box
        sx={{
          fontSize: 13,
          color: "#0D0D0E",
          fontFamily: "Inter Tight",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          "& p": { margin: 0 },
          "& ul, & ol": { margin: 0, paddingLeft: "18px" },
          "& strong, & b": { fontWeight: 600 },
        }}
        dangerouslySetInnerHTML={{ __html: text }}
      />
    </Tooltip>
  );
};

const PRICE_REGEX = /^-?\d{0,6}(\.\d{0,2})?$/;

const formatPriceWithCommas = (raw) => {
  if (raw === "" || raw == null) return "";
  const s = String(raw);
  const sign = s.startsWith("-") ? "-" : "";
  const unsigned = sign ? s.slice(1) : s;
  const [intPart, decPart] = unsigned.split(".");
  const intWithCommas = (intPart || "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return sign + intWithCommas + (decPart !== undefined ? "." + decPart : "");
};

const PriceInput = ({ value, onChange, error }) => {
  const [focused, setFocused] = useState(false);
  const borderColor = error
    ? "#EF4444"
    : focused
    ? "#4D6BF6"
    : "#D7D8D9";
  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      <Typography
        sx={{
          position: "absolute",
          top: -8,
          left: 12,
          background: "#fff",
          padding: "0 6px",
          fontSize: 12,
          color: error ? "#EF4444" : "#0D0D0E",
          fontFamily: "Inter Tight",
          zIndex: 1,
          pointerEvents: "none",
        }}
      >
        Change Request Price
      </Typography>
      <Box
        component="input"
        type="text"
        inputMode="decimal"
        placeholder="Enter change request price"
        value={formatPriceWithCommas(value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          // Strip user-typed commas before validating against the regex.
          const stripped = e.target.value.replace(/,/g, "");
          if (
            stripped === "" ||
            stripped === "-" ||
            PRICE_REGEX.test(stripped)
          ) {
            onChange(stripped);
          }
        }}
        sx={{
          width: "100%",
          border: `1px solid ${borderColor}`,
          borderRadius: "10px",
          padding: "14px 14px",
          fontSize: 14,
          fontFamily: "Inter Tight",
          color: "#0D0D0E",
          background: "#fff",
          outline: "none",
          boxSizing: "border-box",
          transition: "border-color 0.15s",
          "&::placeholder": { color: "#9CA3AF" },
        }}
      />
      {error && (
        <Typography
          sx={{
            fontSize: 12,
            color: "#EF4444",
            fontFamily: "Inter Tight",
            mt: 0.5,
            ml: 1.5,
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

// ---------- Add Change Request modal ----------
function AddChangeRequestModal({ open, onClose, onCreate, submitting }) {
  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [priceErr, setPriceErr] = useState("");
  const [notesErr, setNotesErr] = useState("");

  useEffect(() => {
    if (open) {
      setPrice("");
      setNotes("");
      setPriceErr("");
      setNotesErr("");
    }
  }, [open]);

  const handleCreate = () => {
    let ok = true;
    const priceTrimmed = String(price || "").trim();
    if (!priceTrimmed || priceTrimmed === "-") {
      setPriceErr("Change Request Price is required");
      ok = false;
    } else if (!PRICE_REGEX.test(priceTrimmed)) {
      setPriceErr(
        "Up to 6 digits before the decimal and 2 after (e.g. 123456.78)",
      );
      ok = false;
    } else {
      setPriceErr("");
    }
    // Notes — strip HTML and check there's actual text content.
    const plainNotes = String(notes || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    if (!plainNotes) {
      setNotesErr("Notes are required");
      ok = false;
    } else {
      setNotesErr("");
    }
    if (!ok) return;
    onCreate({
      changeRequestPrice: Number(priceTrimmed),
      notes,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          width: "min(560px, 92vw)",
          padding: "8px",
        },
      }}
    >
      <Box sx={{ padding: "16px 24px 8px" }}>
        <Box
          component="p"
          sx={{
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
            letterSpacing: 0,
          }}
        >
          Add Change Request
        </Box>
      </Box>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <PriceInput
            value={price}
            onChange={setPrice}
            error={priceErr}
          />
          <RichTextEditor
            label="Notes"
            value={notes}
            onChange={setNotes}
            placeholder="Describe the change. You can use bold, bullets, and line breaks."
            error={!!notesErr}
            helperText={notesErr || ""}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 2, pt: 1, justifyContent: "space-between" }}
      >
        <SecondaryButton onClick={onClose} disabled={submitting}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={handleCreate} disabled={submitting}>
          {submitting ? "Creating..." : "Create"}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

// ---------- Table listing ----------
function ChangeRequestsList({ items, onAdd, onRowClick, onCancel }) {
  // Column template — adds a narrow Action column on the right.
  const gridCols = "1fr 1fr 1.4fr 1fr 120px";

  // Empty state — skip the table/cards entirely and only surface the
  // primary CTA so the user can create their first change request.
  if (!items || items.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: { xs: "stretch", md: "flex-start" },
          gap: 1.5,
          py: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            color: "#6B7280",
            fontFamily: "Inter Tight",
          }}
        >
          No change requests yet.
        </Typography>
        <PrimaryButton onClick={onAdd}>Add Change Request</PrimaryButton>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          overflow: "hidden",
          display: { xs: "none", md: "block" },
        }}
      >
        {/* Header — 5 columns: Type | Status | Notes | Price | Action.
            No bottom border so the header reads as part of the same
            visual surface as the rows. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridCols,
            gap: 2,
            padding: "12px 16px",
            fontSize: 13,
            color: "#949DAA",
            fontFamily: "Inter Tight",
            fontWeight: 500,
          }}
        >
          <Box>Type</Box>
          <Box>Status</Box>
          <Box>Notes</Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              justifyContent: "flex-end",
              justifySelf: "end",
              textAlign: "right",
            }}
          >
            Change Request Price ({renderCurrencyIcon("small")})
          </Box>
          <Box sx={{ textAlign: "center", justifySelf: "center" }}>Action</Box>
        </Box>

        {items.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              color: "#9CA3AF",
              fontSize: 14,
              fontFamily: "Inter Tight",
            }}
          >
            No change requests yet.
          </Box>
        ) : (
          items.map((row, idx) => {
            const id = row.changeRequestId ?? row.id;
            const isDraft =
              String(row.status || "").toUpperCase() === "DRAFT";
            return (
              <Box
                key={id ?? idx}
                onClick={() => onRowClick(row)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  gap: 2,
                  padding: "14px 16px",
                  fontSize: 14,
                  color: "#0D0D0E",
                  fontFamily: "Inter Tight",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "background-color 0.1s",
                  "&:hover": { backgroundColor: "#F9FAFB" },
                }}
              >
                <Box>
                  <TypePill type={row.type} />
                </Box>
                <Box>
                  {/* Restored dot-style indicator — the dot's color
                      switches per status (orange/green/red/grey/blue)
                      via CR_STATUS_DOT_COLORS. */}
                  <StatusDot status={row.status} />
                </Box>
                <Box>
                  <NotesCell html={row.notes} />
                </Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    justifyContent: "flex-end",
                    justifySelf: "end",
                  }}
                >
                  {row.changeRequestPrice != null ? (
                    <>
                      {renderCurrencyIcon("black-xs-small")}
                      {formattedAmountWithoutAed(
                        Number(row.changeRequestPrice),
                      )}
                    </>
                  ) : (
                    "-"
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    justifySelf: "center",
                  }}
                >
                  {isDraft ? (
                    <DangerOutlineButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onCancel(row);
                      }}
                    >
                      Delete
                    </DangerOutlineButton>
                  ) : (
                    "-"
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Mobile — stacked cards. */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
        }}
      >
        {items.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              color: "#9CA3AF",
              fontSize: 14,
              fontFamily: "Inter Tight",
            }}
          >
            No change requests yet.
          </Box>
        ) : (
          items.map((row, idx) => {
            const id = row.changeRequestId ?? row.id;
            const isDraft =
              String(row.status || "").toUpperCase() === "DRAFT";
            return (
              <Box
                key={`m-${id ?? idx}`}
                onClick={() => onRowClick(row)}
                sx={{
                  background: "#fff",
                  borderBottom: "1px solid #E5E7EB",
                  py: 1.5,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box>
                  <TypePill type={row.type} />
                </Box>
                {/* Status sits on its own row below the type pill. */}
                <Box>
                  <StatusDot status={row.status} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#9CA3AF",
                      fontFamily: "Inter Tight",
                      mb: 0.25,
                    }}
                  >
                    Notes
                  </Typography>
                  <NotesCell html={row.notes} />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#9CA3AF",
                        fontFamily: "Inter Tight",
                        mb: 0.25,
                      }}
                    >
                      Change Request Price
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#0D0D0E",
                        fontFamily: "Inter Tight",
                      }}
                    >
                      {row.changeRequestPrice != null ? (
                        <>
                          {renderCurrencyIcon("black-xs-small")}
                          {formattedAmountWithoutAed(
                            Number(row.changeRequestPrice),
                          )}
                        </>
                      ) : (
                        "-"
                      )}
                    </Box>
                  </Box>
                  {isDraft && (
                    <DangerOutlineButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onCancel(row);
                      }}
                    >
                      Delete
                    </DangerOutlineButton>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Bottom-left Add CTA — sits below the listing on the left
          edge so the table itself remains the primary content. */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          mt: 1.5,
        }}
      >
        <PrimaryButton onClick={onAdd}>Add Change Request</PrimaryButton>
      </Box>
    </Box>
  );
}

// ---------- Top-level Changes tab ----------
export default function Changes() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project_id") || id;
  const proposalId = id;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // Cancel-confirmation modal state for DRAFT rows. `cancelTarget`
  // holds the row whose action menu was triggered.
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const buildDetailUrl = (crId) => {
    const qs = new URLSearchParams({
      managed_by: "contractor",
      project_id: String(projectId),
    });
    return `/project/details/${proposalId}/changes/${crId}?${qs.toString()}`;
  };

  const loadList = async () => {
    if (!proposalId) return;
    setLoading(true);
    try {
      const res = await listProposalChangeRequests(proposalId);
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setItems(list);
    } catch (e) {
      console.error("Failed to load change requests", e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  // ---------- Cancel a DRAFT change request ----------
  const openCancelModal = (row) => setCancelTarget(row);
  const closeCancelModal = () => {
    if (cancelling) return;
    setCancelTarget(null);
  };
  const confirmCancel = async () => {
    if (!cancelTarget) return;
    const crId = cancelTarget.changeRequestId ?? cancelTarget.id;
    if (crId == null) return;
    setCancelling(true);
    try {
      await actionProposalChangeRequest(proposalId, crId, "CANCEL");
      toast.success("Change request deleted");
      setCancelTarget(null);
      await loadList();
    } catch (e) {
      console.error("Failed to cancel change request", e);
      toast.error(e?.message || "Failed to cancel change request");
    } finally {
      setCancelling(false);
    }
  };

  const handleCreate = async (payload) => {
    setCreating(true);
    try {
      const res = await createProposalChangeRequest(proposalId, payload);
      const created =
        res?.data?.changeRequest ||
        res?.data ||
        res?.changeRequest ||
        res;
      const newId =
        created?.changeRequestId ?? created?.id ?? res?.changeRequestId;
      toast.success("Change request created");
      setModalOpen(false);
      if (newId != null) {
        navigate(buildDetailUrl(newId));
      } else {
        // Fallback: refresh listing in place
        await loadList();
      }
    } catch (e) {
      console.error("Failed to create change request", e);
      toast.error(e?.message || "Failed to create change request");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 6,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <ChangeRequestsList
        items={items}
        onAdd={() => setModalOpen(true)}
        onRowClick={(row) => {
          const crId = row.changeRequestId ?? row.id;
          if (crId != null) navigate(buildDetailUrl(crId));
        }}
        onCancel={openCancelModal}
      />
      <AddChangeRequestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={handleCreate}
        submitting={creating}
      />

      <CancelChangeRequestModal
        open={!!cancelTarget}
        onClose={closeCancelModal}
        onConfirm={confirmCancel}
        cancelling={cancelling}
        actionLabel="Delete"
      />
    </Box>
  );
}
